// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    poNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: InjectableCss;
    // XNG-Changes
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
};

const OrderSummaryCard: FC<Props> = ({ language, order, orderSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;

    // XNG-Changes: Change the structure
    return (
        <>
            <DataTableCell
                {...styles.dataCell}
                {...{ header: translate("Order Date") }}
                data-test-selector="orderSummaryCard_Date"
                className="order-date-grid-item"
            >
                {orderDateDisplay}
            </DataTableCell>
            <DataTableCell
                translate="no"
                {...styles.dataCell}
                {...{ header: translate("Order #") }}
                className="order-number-grid-item"
            >
                {orderNumber && <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />}
            </DataTableCell>
            <DataTableCell
                translate="no"
                {...styles.dataCell}
                {...{ header: translate("PO #") }}
                className="order-po-grid-item"
            >
                {order.customerPO}
            </DataTableCell>
            <DataTableCell {...styles.dataCell} {...{ header: translate("Status") }} className="order-status-grid-item">
                {order.statusDisplay}
            </DataTableCell>
            <DataTableCell
                translate="no"
                {...styles.dataCell}
                {...{ header: translate("Total") }}
                className="order-total-grid-item"
            >
                {order.orderGrandTotalDisplay}
            </DataTableCell>
        </>
    );
};

export default connect(mapStateToProps)(OrderSummaryCard);
